// Physics Playground — a self-contained box3d runtime for the /physics-playground
// page. Deliberately independent of yardPhysics.ts: it owns its own world and
// runtime singleton so the two pages never share mutable world state.
//
// Phase 3b: ported onto the three-box3d library (`box3d-web`). The hand-rolled
// bridge + inline accumulator/sleep-sweep/radial-impulse are replaced by the
// library's World API + FixedStepper / SleepManager / radialImpulse helpers.
// The runtime's public shape (rt.transforms flat layout, rt.brickBodies,
// rt.balls, offset helpers, rt.caps, __playgroundState/__playgroundActions
// contracts) is unchanged so the render layer and E2E bridges are untouched.

import {
  createBox3D,
  probeCapabilities,
  FixedStepper,
  TransformBuffer,
  SleepManager,
  radialImpulse,
  type Box3D,
  type World,
  type BodyHandle,
} from 'box3d-web';

export const PG_FIXED_DT = 1 / 60;
export const PG_SUBSTEPS = 4;

export const MAX_BRICKS = 120;
export const MAX_BALLS = 4;

// Brick full extents — chunkier than the yard's so ~65 bricks read as a hero
// arrangement rather than distant specks (QA round 1: formations were lost in frame).
export const BRICK_FULL = { x: 0.62, y: 0.42, z: 0.62 };
export const BALL_RADIUS = 0.5;
export const BALL_SPEED = 26;

export const BLAST_RADIUS = 4.6;

// Grid pitch: a touch of clearance so freshly spawned bricks don't interpenetrate.
const BX = 0.63;
const BY = 0.427;

const BRICK_SLEEP_SWEEP_INTERVAL = 2;
const BRICK_SLEEP_MOVE_EPS = 0.01;

export type PlaygroundTool = 'flick' | 'ball' | 'blast';

export interface PlaygroundImpact {
  x: number;
  y: number;
  z: number;
  strength: number;
}

export interface PlaygroundCaps {
  awakeBodyCount: boolean;
  bodyCount: boolean;
  setAwake: boolean;
}

interface BrickSpec {
  x: number;
  y: number;
  z: number;
  qy: number;
  qw: number;
  tint: string;
}

export interface PlaygroundRuntime {
  world: World;
  caps: PlaygroundCaps;
  stepper: FixedStepper;
  buffer: TransformBuffer;
  sleep: SleepManager;
  stepMs: number;
  awakeCount: number;
  bodyCountTotal: number;
  brickBodies: number[];
  brickColors: string[];
  balls: number[];
  transforms: Float32Array;
  prevTransforms: Float32Array;
  prevValid: boolean;
  bodyIds: Int32Array;
  bodyIndex: Map<number, number>;
  idsDirty: boolean;
  impacts: PlaygroundImpact[];
  structureVersion: number;
  staticBodyCount: number;
}

let modulePromise: Promise<Box3D> | null = null;
let runtime: PlaygroundRuntime | null = null;
let runtimePromise: Promise<PlaygroundRuntime> | null = null;
let runtimeFailed = false;

function loadModule() {
  if (!modulePromise) {
    modulePromise = createBox3D();
  }
  return modulePromise;
}

export function getExistingPlaygroundRuntime() {
  return runtime;
}

export async function getPlaygroundRuntime() {
  if (runtime) return runtime;
  if (runtimeFailed) throw new Error('Playground physics failed to initialize');

  if (!runtimePromise) {
    runtimePromise = createPlaygroundRuntime()
      .then((created) => {
        runtime = created;
        return created;
      })
      .catch((error: unknown) => {
        runtimeFailed = true;
        runtimePromise = null;
        throw error;
      });
  }

  return runtimePromise;
}

async function createPlaygroundRuntime(): Promise<PlaygroundRuntime> {
  const box3d = await loadModule();
  const world = box3d.createWorld({ gravity: [0, -9.8, 0] });
  const probed = probeCapabilities(world);
  const caps: PlaygroundCaps = {
    awakeBodyCount: probed.awakeBodyCount,
    bodyCount: probed.bodyCount,
    setAwake: probed.setAwake,
  };

  const rt: PlaygroundRuntime = {
    world,
    caps,
    stepper: new FixedStepper({ fixedDt: PG_FIXED_DT, substeps: PG_SUBSTEPS, maxStepsPerFrame: 3 }),
    buffer: new TransformBuffer(MAX_BRICKS + MAX_BALLS),
    sleep: new SleepManager(world, {
      sweepIntervalSec: BRICK_SLEEP_SWEEP_INTERVAL,
      moveThreshold: BRICK_SLEEP_MOVE_EPS,
    }),
    stepMs: 0,
    awakeCount: 0,
    bodyCountTotal: 0,
    brickBodies: [],
    brickColors: [],
    balls: [],
    transforms: new Float32Array(0),
    prevTransforms: new Float32Array(0),
    prevValid: false,
    bodyIds: new Int32Array(0),
    bodyIndex: new Map(),
    idsDirty: true,
    impacts: [],
    structureVersion: 0,
    staticBodyCount: 0,
  };

  addStatics(rt);
  buildFormations(rt);
  return rt;
}

function addStatics(rt: PlaygroundRuntime) {
  const ground = rt.world.createBody({ type: 'static', position: [0, -0.6, 0] });
  rt.world.addBox(ground, [60, 0.6, 60], { density: 0, friction: 0.9, restitution: 0.02 });
  rt.staticBodyCount += 1;
}

// --- formations ---

// Terracotta family with gold accent bands / corners and cream highlights,
// matching the /wrecking-yard palette.
const TERRA = ['#b0472f', '#c25a38', '#a03e2a'];
const GOLD = '#d9a53f';
const CREAM = '#e2cfa8';

function seededUnit(seed: number) {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

function terra(seed: number): string {
  return TERRA[Math.floor(seededUnit(seed) * TERRA.length) % TERRA.length];
}

// Three formations placed around the origin so they read as deliberate:
//   • a running-bond wall (back-left)
//   • a stepped pyramid (center)
//   • a slender tower (right)
function buildWallSpecs(cx: number, cz: number): BrickSpec[] {
  const specs: BrickSpec[] = [];
  const cols = 5;
  const rows = 5;
  const half = (cols - 1) / 2;
  for (let row = 0; row < rows; row += 1) {
    const y = row * BY + BRICK_FULL.y / 2;
    // Running bond: offset alternate rows by half a brick, trimming the ends.
    const stagger = row % 2 === 0 ? 0 : 0.5;
    for (let col = 0; col < cols; col += 1) {
      if (stagger && (col === 0 || col === cols - 1)) continue; // keep ends flush
      const x = cx + (col - half + stagger) * BX;
      // Gold string-course every other row; cream on the two ends of the base.
      let tint = terra(row * 71 + col * 13);
      if (row === rows - 1) tint = GOLD;
      else if (row === 0 && (col === 0 || col === cols - 1)) tint = CREAM;
      specs.push({ x, y, z: cz, qy: 0, qw: 1, tint });
    }
  }
  return specs;
}

function buildPyramidSpecs(cx: number, cz: number): BrickSpec[] {
  const specs: BrickSpec[] = [];
  const base = 4; // 4x4 base, shrinking by one ring each layer
  for (let layer = 0; layer < base; layer += 1) {
    const n = base - layer;
    const half = (n - 1) / 2;
    const y = layer * BY + BRICK_FULL.y / 2;
    for (let ix = 0; ix < n; ix += 1) {
      for (let iz = 0; iz < n; iz += 1) {
        const apex = n === 1;
        const corner = (ix === 0 || ix === n - 1) && (iz === 0 || iz === n - 1);
        const tint = apex ? GOLD : corner ? CREAM : terra(layer * 131 + ix * 17 + iz * 7);
        specs.push({
          x: cx + (ix - half) * BX,
          y,
          z: cz + (iz - half) * BX,
          qy: 0,
          qw: 1,
          tint,
        });
      }
    }
  }
  return specs;
}

function buildTowerSpecs(cx: number, cz: number): BrickSpec[] {
  const specs: BrickSpec[] = [];
  const layers = 9;
  // 2 bricks per layer, quarter-turned every other layer for a woven look.
  for (let layer = 0; layer < layers; layer += 1) {
    const y = layer * BY + BRICK_FULL.y / 2;
    const rot = layer % 2 === 0;
    const offsets: Array<[number, number]> = rot
      ? [
          [-0.35, 0],
          [0.35, 0],
        ]
      : [
          [0, -0.35],
          [0, 0.35],
        ];
    const yaw = rot ? 0 : Math.PI / 2;
    const band = layer % 3 === 2;
    for (let i = 0; i < offsets.length; i += 1) {
      const [ox, oz] = offsets[i];
      const tint = layer === layers - 1 ? GOLD : band ? CREAM : terra(layer * 53 + i * 11);
      specs.push({
        x: cx + ox,
        y,
        z: cz + oz,
        qy: Math.sin(yaw / 2),
        qw: Math.cos(yaw / 2),
        tint,
      });
    }
  }
  return specs;
}

function formationSpecs(): BrickSpec[] {
  // Arranged for the default camera (azimuth ~0.5-0.66 rad): wall far LEFT of
  // the pyramid — not behind it — so all three silhouettes have clear air in
  // the initial view (QA round 2: wall+pyramid read as one jumbled structure).
  // Screen-space centers land at roughly -4.2 / 0 / +3.3 on the view's
  // horizontal axis; physical gaps stay >2m so islands never merge.
  return [
    ...buildWallSpecs(-4.7, 0.3),
    ...buildPyramidSpecs(0.3, 0.7),
    ...buildTowerSpecs(3.1, -1.3),
  ];
}

function buildFormations(rt: PlaygroundRuntime) {
  const specs = formationSpecs().slice(0, MAX_BRICKS);
  rt.brickBodies = specs.map((spec) => {
    const bodyId = rt.world.createBody({
      type: 'dynamic',
      position: [spec.x, spec.y, spec.z],
      rotation: [0, spec.qy, 0, spec.qw],
    });
    rt.world.addBox(bodyId, [BRICK_FULL.x / 2, BRICK_FULL.y / 2, BRICK_FULL.z / 2], {
      density: 3,
      friction: 0.78,
      restitution: 0.04,
    });
    return bodyId;
  });
  rt.brickColors = specs.map((spec) => spec.tint);
  rt.structureVersion += 1;
  rt.idsDirty = true;

  // Force the fresh formation asleep: settle contacts for two fixed steps, then
  // sleep every brick so the page idles at awake≈0 / step<1ms immediately.
  if (rt.caps.setAwake) {
    rt.world.step(PG_FIXED_DT, PG_SUBSTEPS);
    rt.world.step(PG_FIXED_DT, PG_SUBSTEPS);
    rt.brickBodies.forEach((bodyId) => rt.world.sleepBody(bodyId as BodyHandle));
  }
}

function destroyDynamic(rt: PlaygroundRuntime) {
  rt.brickBodies.forEach((bodyId) => rt.world.destroyBody(bodyId as BodyHandle));
  rt.brickBodies = [];
  rt.brickColors = [];
  rt.balls.forEach((bodyId) => rt.world.destroyBody(bodyId as BodyHandle));
  rt.balls = [];
  rt.impacts.length = 0;
  rt.idsDirty = true;
}

export function resetPlayground(rt: PlaygroundRuntime) {
  destroyDynamic(rt);
  buildFormations(rt);
}

// --- tools ---

// Flick: wake the target brick's island, then punch it along the camera ray.
export function flickBrick(rt: PlaygroundRuntime, index: number, dir: { x: number; y: number; z: number }) {
  const bodyId = rt.brickBodies[index];
  if (!bodyId) return;
  rt.world.wakeBody(bodyId as BodyHandle);
  const len = Math.hypot(dir.x, dir.y, dir.z) || 1;
  // Tuned for the 0.62 brick (~1.9x the mass of the original 0.5 brick).
  const impulse = 12;
  const offset = index * 7;
  const bx = rt.transforms[offset];
  const by = rt.transforms[offset + 1];
  const bz = rt.transforms[offset + 2];
  rt.world.applyImpulse(
    bodyId as BodyHandle,
    [
      (dir.x / len) * impulse,
      (dir.y / len) * impulse + 1.1, // slight lift so it topples rather than slides
      (dir.z / len) * impulse,
    ],
    [bx, by, bz],
  );
}

export function brickBodyIdAt(rt: PlaygroundRuntime, index: number): number {
  return rt.brickBodies[index] ?? 0;
}

export function throwBall(
  rt: PlaygroundRuntime,
  origin: { x: number; y: number; z: number },
  dir: { x: number; y: number; z: number },
) {
  const len = Math.hypot(dir.x, dir.y, dir.z) || 1;
  const bodyId = rt.world.createBody({ type: 'dynamic', position: [origin.x, origin.y, origin.z], ccd: true });
  rt.world.addSphere(bodyId, BALL_RADIUS, { density: 22, friction: 0.5, restitution: 0.18 });
  rt.world.setLinearVelocity(bodyId, [
    (dir.x / len) * BALL_SPEED,
    (dir.y / len) * BALL_SPEED,
    (dir.z / len) * BALL_SPEED,
  ]);
  rt.balls.push(bodyId);
  while (rt.balls.length > MAX_BALLS) {
    const oldest = rt.balls.shift();
    if (oldest) rt.world.destroyBody(oldest as BodyHandle);
  }
  rt.idsDirty = true;
}

// Blast: native explode is a no-op on sleeping bodies, so wake + JS radial
// impulse sweep with quadratic falloff (via the library's radialImpulse helper,
// which reproduces the old blastAt math: strength 8.5, upwardBias 1.1).
export function blastAt(rt: PlaygroundRuntime, x: number, y: number, z: number) {
  radialImpulse(rt.world, rt.brickBodies as BodyHandle[], rt.buffer, {
    center: [x, y, z],
    radius: BLAST_RADIUS,
    strength: 8.5,
    falloff: 'quadratic',
    upwardBias: 1.1,
  });
}

export function bodyPosition(rt: PlaygroundRuntime, bodyId: number): { x: number; y: number; z: number } | null {
  const index = rt.bodyIndex.get(bodyId);
  if (index == null) return null;
  const offset = index * 7;
  return { x: rt.transforms[offset], y: rt.transforms[offset + 1], z: rt.transforms[offset + 2] };
}

// --- frame advance ---

export function advancePlaygroundPhysics(rt: PlaygroundRuntime, delta: number) {
  let stepMsTotal = 0;
  const stepped = rt.stepper.advance(delta, () => {
    const t0 = performance.now();
    rt.world.step(PG_FIXED_DT, PG_SUBSTEPS);
    stepMsTotal += performance.now() - t0;
  });
  if (stepped) {
    rt.stepMs = stepMsTotal;
  }

  collectImpacts(rt);

  if (rt.idsDirty) {
    rebuildBuffers(rt);
  }

  if (rt.bodyIds.length > 0) {
    if (stepped && rt.prevValid) {
      rt.prevTransforms.set(rt.transforms);
    }
    rt.buffer.readInto(rt.world);
    if (stepped) {
      computeAwake(rt);
      // Island-aware sleep discipline: force-sleep freshly spawned bodies after
      // the settle window, then the periodic <threshold sweep.
      rt.sleep.forceSleepSettled();
      rt.sleep.sweep(rt.stepper.simTime);
      rt.prevValid = true;
    }
  }

  rt.bodyCountTotal = rt.caps.bodyCount
    ? rt.world.bodyCount()
    : rt.staticBodyCount + rt.brickBodies.length + rt.balls.length;
}

function computeAwake(rt: PlaygroundRuntime) {
  if (rt.caps.awakeBodyCount) {
    rt.awakeCount = rt.world.awakeBodyCount();
    return;
  }
  if (!rt.prevValid) {
    rt.awakeCount = rt.bodyIds.length;
    return;
  }
  let awake = 0;
  const eps = 0.0006;
  for (let i = 0; i < rt.bodyIds.length; i += 1) {
    const offset = i * 7;
    const dx = Math.abs(rt.transforms[offset] - rt.prevTransforms[offset]);
    const dy = Math.abs(rt.transforms[offset + 1] - rt.prevTransforms[offset + 1]);
    const dz = Math.abs(rt.transforms[offset + 2] - rt.prevTransforms[offset + 2]);
    if (dx + dy + dz > eps) awake += 1;
  }
  rt.awakeCount = awake;
}

function collectImpacts(rt: PlaygroundRuntime) {
  const events = rt.world.drainContactBeginEvents();
  for (const event of events) {
    if (event.approachSpeed < 2.2) continue;
    const pos = bodyPosition(rt, event.bodyA) ?? bodyPosition(rt, event.bodyB);
    if (!pos) continue;
    if (rt.impacts.length < 8) {
      rt.impacts.push({ x: pos.x, y: pos.y, z: pos.z, strength: event.approachSpeed });
    }
  }
}

function rebuildBuffers(rt: PlaygroundRuntime) {
  const ids: number[] = [];
  rt.brickBodies.forEach((bodyId) => ids.push(bodyId));
  rt.balls.forEach((bodyId) => ids.push(bodyId));

  // Rebuild the TransformBuffer's tracked set in [bricks..., balls...] insertion
  // order so the flat rt.transforms offsets the render layer relies on stay
  // stable. A fresh buffer each rebuild mirrors the old fresh-array behavior and
  // avoids TransformBuffer's remove-renumbering surprises.
  rt.buffer = new TransformBuffer(Math.max(ids.length, 1));
  for (const bodyId of ids) rt.buffer.add(bodyId as BodyHandle);
  rt.buffer.rebuild();

  rt.bodyIds = rt.buffer.ids;
  rt.transforms = rt.buffer.transforms;
  rt.prevTransforms = new Float32Array(ids.length * 7);
  rt.prevValid = false;
  rt.bodyIndex = new Map(ids.map((bodyId, index) => [bodyId, index]));

  // Re-arm the sleep discipline for the current brick set (bricks only; balls
  // are transient projectiles that box3d auto-sleeps).
  rt.sleep.watch(rt.brickBodies as BodyHandle[], rt.buffer);
  rt.idsDirty = false;
}

// Buffer layout: [bricks..., balls...]
export function ballTransformOffset(rt: PlaygroundRuntime, index: number) {
  return (rt.brickBodies.length + index) * 7;
}
